/**
 * A function that adds query params to the path.
 *
 * @param pathname - The pathname the params are added to.
 * @param paramsObject - An object of optional additional params.
 * @returns - The pathname and query.
 *
 * @example
 * ```tsx
 * import { useContructURL } from 'src/utils/construct-url.ts
 *
 * const loginUrl = useConstructUrl('/', { type: 'add_account' });
 * router.push(loginURL)
 * ```
 */

import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { UrlObject } from 'url';

export function useConstructUrl(pathname: string, additionalParams: Record<string, string> = {}): UrlObject {
  const router = useRouter();
  const clientId = router.query.client_id as string;

  const url = useMemo(
    () => ({
      pathname,
      query: {
        app_ref: router.query.app_ref,
        client_id: clientId,
        intent: router.query.intent,
        redirect_uri: router.query.redirect_uri,
        ...additionalParams,
      },
    }),
    [router.query, additionalParams, pathname, clientId]
  );

  return url;
}
